/**
 * 参数管理器。
 *
 * ## 这个文件里最重要的三个协议事实
 *
 * **1. 全量拉取一定会丢包，所以必须按 index 记账并重试。**
 * `PARAM_REQUEST_LIST` 之后飞控会把整张参数表当**流**推过来（真实的 4.7 Copter 有 1600+ 条，
 * 57600 波特下要几十秒）。这一路上丢几帧是常态而不是异常，而且丢帧是**静默**的：
 * 没有序号、没有 ACK，缺了就是缺了。所以这里按 `param_index` 记账，
 * 静默超时后逐个 `PARAM_REQUEST_READ`（按 index）补齐，
 * 补不齐就如实报"部分失败"并把缺口列出来 —— 界面显示"下载完成"而实际少了 30 个参数，
 * 是会让用户基于不完整的数据做决定的。
 *
 * **2. `PARAM_SET` 没有 `COMMAND_ACK`。**
 * MAVLink 的 PARAM_SET 是"发送即忘"的消息，ArduPilot 的回应是**再发一条 `PARAM_VALUE`**。
 * 所以"设置成功"的判据只能是：收到同名参数的回读值，且它与期望值在浮点容差内相等
 * （容差见 `float.ts`）。**超时没等到回读就等于失败** —— 绝不能"发了就报告成功"，
 * 那会让用户以为参数已经改了而实际上没有。
 *
 * **3. "没回应"与"回应了但值不同"是两件不同的事。**
 * 后者说明飞控收到了、但拒绝了或做了范围钳制（例如把 5000 钳到 3000）。
 * 界面上这两种失败要给出不同的建议：前者去查链路/参数名，后者去看取值范围。
 * 所以 `ParamSetResult` 里带 `status` **和** `actual`（实际回读值）。
 *
 * ## 订阅的生命周期
 *
 * `vehicle.events` 上的订阅是**引用计数**的：下载、`watch()`、等待确认的 `set()`
 * 各自持有一份。因此：
 *   - 下载中途取消（用户切机型、关页面）→ 引用归零 → 监听器被摘掉，不会泄漏；
 *   - `dispose()` 之后 `listenerCount('paramValue')` 归零，测试可以直接断言这一点。
 * 订阅懒创建也是刻意的：没人在用的管理器不该在车辆事件总线上占一个位置。
 */
import { enumValue, type MessageValues } from '@hgc/mavlink';

import { createEventBus, type EventBus, type Unsubscribe } from '../events';
import type { ParamValueEvent, Vehicle } from '../vehicle';
import {
  diffParamValues,
  paramValueMap,
  type ParamDiff,
  type ParamDiffKind,
  type ParamFileEntry,
  type ParamValueSource,
} from './file';
import { isFiniteParamValue, paramValuesEqual } from './float';
import {
  validateParamValue,
  type ParamMetadataEntry,
  type ParamMetadataIndex,
  type ParamValidationResult,
} from './metadata';

// ---------------------------------------------------------------------------
// 默认值
// ---------------------------------------------------------------------------

/**
 * 一轮等待 PARAM_VALUE 的**静默**超时。
 *
 * 注意它是"距最后一次收到**新**参数"的间隔，不是整轮的总时长 ——
 * 1600 个参数要推一分钟，用总时长做超时会稳定地判失败。
 * 1500 ms 的依据：参数流在链路上是连续的（每帧几十字节），
 * 真的还在推就不会有 1.5 秒的空档；而 1.5 秒对操作员来说也还在"等一下就好"的范围里。
 */
export const DEFAULT_DOWNLOAD_IDLE_TIMEOUT_MS = 1500;

/** 单条 `PARAM_SET` 等待回读的超时。飞控回一条消息的正常耗时是毫秒级。 */
export const DEFAULT_SET_TIMEOUT_MS = 1200;

/** 全量下载的最大轮数（首发 + 补缺轮） */
export const DEFAULT_MAX_ROUNDS = 3;

/** 多个参数连续写入时，每写一条之间的间隔（毫秒）。 */
export const DEFAULT_APPLY_INTERVAL_MS = 50;

// ---------------------------------------------------------------------------
// 定时器
// ---------------------------------------------------------------------------

/**
 * 定时器抽象。
 *
 * 测试用的假时钟要能显式推进时间，`setTimeout` 做不到这件事而 `vi.useFakeTimers()`
 * 会连测试自身的 `await flush()` 一起冻住。注入一个"延时回调"是最小接口。
 */
export interface ParamScheduler {
  after(ms: number, callback: () => void): Unsubscribe;
}

export function createTimeoutScheduler(): ParamScheduler {
  return {
    after: (ms, callback) => {
      const timer = setTimeout(callback, ms);
      return () => clearTimeout(timer);
    },
  };
}

// ---------------------------------------------------------------------------
// 数据模型
// ---------------------------------------------------------------------------

export interface ParamEntry {
  readonly name: string;
  readonly value: number;
  /** 飞控自报的 `param_index`；收不到时为 null */
  readonly index: number | null;
  /** `MAV_PARAM_TYPE`；收不到时为 null */
  readonly type: number | null;
  /** 最近一次收到该参数的时刻 */
  readonly at: number;
}

/**
 * `changed` 事件的来源。
 *
 * "值变了"这个事实要分得清是谁改的：全量下载是"第一次看到"，
 * `set` 是我们自己刚写的，`value` 是飞控侧（或另一台地面站）改的。
 * 界面据此决定要不要弹提示 —— 只有 `value` 值得提醒用户"参数被外部修改了"。
 */
export type ParamChangeSource = 'download' | 'value' | 'set';

export interface ParamChangedEvent {
  readonly name: string;
  readonly value: number;
  /** 变化前的值；首次出现为 null */
  readonly previous: number | null;
  readonly index: number | null;
  readonly source: ParamChangeSource;
  readonly at: number;
}

export type ParamDownloadPhase =
  /** 已发出 `PARAM_REQUEST_LIST` */
  | 'requesting'
  /** 正在接收参数流 */
  | 'downloading'
  /** 正在按 index 补缺 */
  | 'retrying'
  | 'complete'
  /** 有缺口且重试用尽 —— 缺口在 `missingIndexes` 里如实列出 */
  | 'partial'
  | 'cancelled'
  | 'failed';

export interface ParamDownloadProgress {
  readonly phase: ParamDownloadPhase;
  /** 已收到的**不同** index 数（重传不重复计数） */
  readonly received: number;
  /** 飞控自报的 `param_count`；还没收到任何 PARAM_VALUE 时为 null */
  readonly total: number | null;
  /** 缺口数量 */
  readonly missing: number;
  /** 第几轮，1 = 首发列表请求 */
  readonly round: number;
  readonly at: number;
  /** 缺口明细（只在 `retrying` / `partial` 时给出，逐条进度事件里带它太浪费） */
  readonly missingIndexes: readonly number[] | null;
  readonly reason: string | null;
}

export type ParamDownloadStatus = 'complete' | 'partial' | 'cancelled' | 'failed';

export interface ParamDownloadResult {
  readonly status: ParamDownloadStatus;
  /** 收到的不同 index 数（= 实收参数条数） */
  readonly received: number;
  /** 飞控自报的参数总数 */
  readonly total: number | null;
  /** 没收到、重试也没补上的 index（`partial` 时非空） */
  readonly missing: readonly number[];
  readonly rounds: number;
  readonly elapsedMs: number;
  readonly reason: string | null;
}

export interface ParamDownloadOptions {
  /** 覆盖这一轮的静默超时 */
  readonly timeoutMs?: number;
}

export type ParamSetStatus =
  /** 收到同名回读且值在容差内相等 —— 这是唯一的"成功" */
  | 'applied'
  /** 收到了回读但值不同（飞控拒绝、或做了范围钳制） */
  | 'value-mismatch'
  /** 超时没等到任何回读 —— 未确认，必须当成失败（含参数名不存在被忽略的情况） */
  | 'no-response'
  /** 元数据标记只读，压根没发 */
  | 'read-only'
  /** 值本身非法（非有限数等），压根没发 */
  | 'invalid'
  /** 链路没打开或发送失败 */
  | 'send-failed'
  | 'cancelled'
  | 'disposed';

export interface ParamSetResult {
  readonly status: ParamSetStatus;
  readonly name: string;
  /** 期望写入的值 */
  readonly requested: number;
  /** 回读到的实际值；没等到为 null —— "值不符"时必须看它 */
  readonly actual: number | null;
  readonly at: number;
  readonly elapsedMs: number;
  readonly message: string | null;
  /** 不阻断的校验提示（超范围、非法枚举值、需要重启…） */
  readonly warnings: readonly ParamValidationIssueLike[];
}

/** 与 `metadata.ts` 的 `ParamValidationIssue` 同形，这里重新导出是为了让界面只 import 管理器的类型 */
export interface ParamValidationIssueLike {
  readonly kind: string;
  readonly message: string;
}

export interface ParamSetOptions {
  readonly timeoutMs?: number;
}

export interface ParamApplyProgress {
  readonly done: number;
  readonly total: number;
  readonly applied: number;
  readonly failed: number;
  readonly skipped: number;
  readonly lastResult: ParamSetResult | null;
  readonly at: number;
}

export interface ParamApplyOptions {
  /** 与当前值相同的项直接跳过（默认 true）—— 写 1600 个"没变"的参数要花几分钟 */
  readonly skipUnchanged?: boolean;
  /** 遇到第一个失败就停（默认 false：继续写完，最后统一报告） */
  readonly stopOnFailure?: boolean;
  readonly timeoutMs?: number;
  /** 两条之间的间隔（毫秒，默认 50）—— 连续写太快会把飞控的参数写队列打满 */
  readonly intervalMs?: number;
}

export interface ParamApplyReport {
  readonly total: number;
  readonly applied: number;
  readonly failed: number;
  readonly skipped: number;
  /** 失败与未执行（`stopOnFailure`）的明细 */
  readonly results: readonly ParamSetResult[];
  readonly stoppedEarly: boolean;
}

export interface ParamListItem {
  readonly name: string;
  readonly value: number;
  readonly index: number | null;
  readonly type: number | null;
  readonly at: number;
  /** 元数据；没加载时是 null —— **不得**因为它是 null 就拒绝显示参数 */
  readonly metadata: ParamMetadataEntry | null;
  /** 显示名（元数据的 `DisplayName`，没有则等于 `name`） */
  readonly label: string;
  readonly units: string | null;
  /** 中文说明（注入的翻译表）；没有时为 null */
  readonly descriptionZh: string | null;
  readonly description: string | null;
  readonly group: string | null;
  readonly groupLabel: string | null;
  readonly readOnly: boolean;
  readonly rebootRequired: boolean;
}

export interface ParamQueryOptions {
  readonly group?: string;
  readonly limit?: number;
}

export type ParamEvents = {
  /** 全量下载进度（界面画进度条用） */
  download: ParamDownloadProgress;
  /** 某条参数的值发生变化（含首次出现）；重复推送同一个值**不会**再次触发 */
  changed: ParamChangedEvent;
  /** 一次 PARAM_SET 的结论 */
  set: ParamSetResult;
  /** 连续写入（导入参数文件）的进度 */
  apply: ParamApplyProgress;
  /** 元数据被替换（界面据此重算说明与校验） */
  metadata: ParamMetadataIndex | null;
};

export interface ParameterManagerOptions {
  readonly vehicle: Vehicle;
  readonly now?: () => number;
  readonly scheduler?: ParamScheduler;
  readonly downloadTimeoutMs?: number;
  readonly setTimeoutMs?: number;
  readonly maxRounds?: number;
  /** 初始元数据（可以在之后用 `setMetadata` 替换） */
  readonly metadata?: ParamMetadataIndex | null;
  readonly onListenerError?: (error: unknown, type: string) => void;
}

/** 内部：一次全量下载的状态 */
interface DownloadRun {
  cancelled: boolean;
  /** 本轮已收到的不同 index */
  readonly received: Set<number>;
  /** 飞控自报的总数（累计取最新一次非零值） */
  total: number | null;
  round: number;
  phase: ParamDownloadPhase;
  readonly startedAt: number;
  /** 最后一次收到**新**参数的时刻，静默超时以它为基准 */
  lastProgressAt: number;
  idleTimer: Unsubscribe | null;
  /** 结束当前轮（收齐 / 静默超时 / 取消） */
  wake: ((outcome: 'complete' | 'idle' | 'cancelled') => void) | null;
  timeoutMs: number;
  promise: Promise<ParamDownloadResult> | null;
}

interface PendingSet {
  readonly name: string;
  readonly requested: number;
  readonly at: number;
  readonly warnings: readonly ParamValidationIssueLike[];
  sentAt: number | null;
  timer: Unsubscribe | null;
  settled: boolean;
  settle: (status: ParamSetStatus, actual: number | null, message: string | null) => void;
}

interface MutableEntry {
  name: string;
  value: number;
  index: number | null;
  type: number | null;
  at: number;
}

let paramTypeReal32Cache: number | null = null;

/**
 * `MAV_PARAM_TYPE_REAL32`。从生成的枚举表取，取不到才退回 9 ——
 * 与 `vehicle.ts` 里查 `EKF_STATUS_FLAGS` 的思路一致：
 * 硬编码一个协议常量，在表与代码不同步时会静默地发错类型。
 */
function paramTypeReal32(): number {
  paramTypeReal32Cache ??= enumValue('MAV_PARAM_TYPE', 'MAV_PARAM_TYPE_REAL32') ?? 9;
  return paramTypeReal32Cache;
}

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** 参数值的可读文本（错误消息里用，避免出现 0.30000000000000004） */
function text(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toPrecision(7);
}

export class ParameterManager {
  readonly vehicle: Vehicle;
  readonly events: EventBus<ParamEvents>;

  private readonly now: () => number;
  private readonly scheduler: ParamScheduler;
  private readonly downloadTimeoutMs: number;
  private readonly setTimeoutMs: number;
  private readonly maxRounds: number;

  private readonly entries = new Map<string, MutableEntry>();
  /** index → 参数名，用于把"收齐了没有"问清楚 */
  private readonly indexToName = new Map<number, string>();
  private totalParams: number | null = null;

  private metadata: ParamMetadataIndex | null;

  /** `paramValue` 订阅的引用计数（下载 / watch / 等待确认 各持一份） */
  private subscriptionCount = 0;
  private offParamValues: Unsubscribe | null = null;

  private pendingSets: PendingSet[] = [];
  private run: DownloadRun | null = null;
  /**
   * `apply()` 正在等待的两条写入之间的间隔。
   *
   * 必须存下来：定时器是这个管理器持有的资源，`dispose()` 收不掉它的话，
   * 销毁之后还要空等最多一个 interval 才轮到循环顶部的 `disposed` 判断。
   */
  private applySleep: { readonly cancel: Unsubscribe; readonly resolve: () => void } | null = null;
  private disposed = false;

  constructor(options: ParameterManagerOptions) {
    this.vehicle = options.vehicle;
    this.now = options.now ?? (() => Date.now());
    this.scheduler = options.scheduler ?? createTimeoutScheduler();
    this.downloadTimeoutMs = options.downloadTimeoutMs ?? DEFAULT_DOWNLOAD_IDLE_TIMEOUT_MS;
    this.setTimeoutMs = options.setTimeoutMs ?? DEFAULT_SET_TIMEOUT_MS;
    this.maxRounds = Math.max(1, options.maxRounds ?? DEFAULT_MAX_ROUNDS);
    this.metadata = options.metadata ?? null;
    this.events = createEventBus<ParamEvents>(
      options.onListenerError === undefined ? {} : { onListenerError: options.onListenerError },
    );
  }

  // -------------------------------------------------------------------------
  // 读取
  // -------------------------------------------------------------------------

  /** 已知参数条数（= 已收到的不同参数名数） */
  get count(): number {
    return this.entries.size;
  }

  /** 飞控自报的参数总数；还没收到过 `PARAM_VALUE` 时为 null */
  get expectedCount(): number | null {
    return this.totalParams;
  }

  has(name: string): boolean {
    return this.entries.has(name);
  }

  get(name: string): ParamEntry | undefined {
    return this.entries.get(name);
  }

  /** `param_index` → 参数名。界面显示"第 20 号参数"时用得上。 */
  nameOf(index: number): string | undefined {
    return this.indexToName.get(index);
  }

  /** 全部参数（按名字升序 —— 界面列表要稳定，按到达顺序排会随机跳动） */
  list(): readonly ParamListItem[] {
    return this.sortedEntries().map((entry) => this.toListItem(entry));
  }

  /**
   * 按名字/显示名/分组过滤。
   *
   * `limit` 默认 100：参数表有 1600+ 条，把它整份塞进 DOM 会让参数页在低端设备上卡住。
   */
  query(searchText = '', options: ParamQueryOptions = {}): readonly ParamListItem[] {
    const limit = options.limit ?? 100;
    const needle = searchText.trim().toUpperCase();
    const out: ParamListItem[] = [];
    for (const entry of this.sortedEntries()) {
      if (out.length >= limit) break;
      const item = this.toListItem(entry);
      if (options.group !== undefined && item.group !== options.group) continue;
      if (needle.length > 0 && !matchesItem(item, needle)) continue;
      out.push(item);
    }
    return out;
  }

  /** 可导出为 `.param` 的项（按名字升序） */
  fileEntries(): readonly ParamFileEntry[] {
    return this.sortedEntries().map((entry) => ({ name: entry.name, value: entry.value }));
  }

  /** 当前值的 name → value 视图（diff 与导出用） */
  values(): ReadonlyMap<string, number> {
    const map = new Map<string, number>();
    for (const entry of this.entries.values()) map.set(entry.name, entry.value);
    return map;
  }

  /** 当前机体值 vs 参考值（文件 / 默认值 / 另一台机体的快照） */
  diff(reference: ParamValueSource, kinds?: readonly ParamDiffKind[]): ParamDiff {
    return diffParamValues(
      this.values(),
      paramValueMap(reference),
      kinds === undefined ? {} : { kinds },
    );
  }

  // -------------------------------------------------------------------------
  // 元数据
  // -------------------------------------------------------------------------

  get paramMetadata(): ParamMetadataIndex | null {
    return this.metadata;
  }

  /**
   * 换一份元数据索引（异步加载完成后调用）。
   *
   * 传 null 是合法的，含义是"退回到没有元数据的状态"，参数照常显示。
   */
  setMetadata(index: ParamMetadataIndex | null): void {
    this.metadata = index;
    this.events.emit('metadata', index);
  }

  /** 用元数据校验一个待写入的值（界面上的输入框实时提示用） */
  validate(name: string, value: number): ParamValidationResult {
    return validateParamValue(value, this.metadata?.get(name) ?? null);
  }

  // -------------------------------------------------------------------------
  // 订阅
  // -------------------------------------------------------------------------

  /**
   * 持续跟踪车辆推送的 `PARAM_VALUE`（增量更新）。
   *
   * 返回取消函数。**全量下载不需要调用它**：下载自己会订阅。
   * 调用它的场景是"参数页已经打开，用户在看着这些值"。
   */
  watch(): Unsubscribe {
    this.retainSubscription();
    let active = true;
    return () => {
      if (!active) return;
      active = false;
      this.releaseSubscription();
    };
  }

  private retainSubscription(): void {
    this.subscriptionCount += 1;
    if (this.offParamValues === null && !this.disposed) {
      this.offParamValues = this.vehicle.events.on('paramValue', (event) =>
        this.handleParamValue(event),
      );
    }
  }

  private releaseSubscription(): void {
    if (this.subscriptionCount > 0) this.subscriptionCount -= 1;
    if (this.subscriptionCount === 0) {
      this.offParamValues?.();
      this.offParamValues = null;
    }
  }

  // -------------------------------------------------------------------------
  // 全量下载
  // -------------------------------------------------------------------------

  /**
   * 全量下载参数表。
   *
   * 并发调用会**共用**同一次下载（界面上重复点"下载"不该在链路上发两遍列表请求）。
   * 取消用 `cancel()`；`dispose()` 也会结束它。
   */
  download(options: ParamDownloadOptions = {}): Promise<ParamDownloadResult> {
    if (this.disposed) {
      return Promise.resolve({
        status: 'failed',
        received: 0,
        total: null,
        missing: [],
        rounds: 0,
        elapsedMs: 0,
        reason: '管理器已销毁',
      });
    }
    const running = this.run;
    if (running !== null && running.promise !== null) return running.promise;

    const startedAt = this.now();
    const run: DownloadRun = {
      cancelled: false,
      received: new Set<number>(),
      total: null,
      round: 0,
      phase: 'requesting',
      startedAt,
      lastProgressAt: startedAt,
      idleTimer: null,
      wake: null,
      timeoutMs: options.timeoutMs ?? this.downloadTimeoutMs,
      promise: null,
    };
    // 先登记再启动：`runDownload` 在第一个 await 之前就会发事件，
    // 而收到 PARAM_VALUE 时要能立刻看到这个 run
    this.run = run;
    this.retainSubscription();
    const promise = this.runDownload(run)
      .catch((error) => this.finish(run, 'failed', describeError(error)))
      .finally(() => {
        this.clearIdleTimer(run);
        if (this.run === run) this.run = null;
        this.releaseSubscription();
      });
    run.promise = promise;
    return promise;
  }

  /** 取消正在进行的全量下载。已经收到的值会保留（它们是有效的参数值）。 */
  cancel(): void {
    const run = this.run;
    if (run === null || run.cancelled) return;
    run.cancelled = true;
    run.wake?.('cancelled');
  }

  private async runDownload(run: DownloadRun): Promise<ParamDownloadResult> {
    this.emitProgress(run, 'requesting', null, null);
    for (run.round = 1; run.round <= this.maxRounds; run.round += 1) {
      if (run.cancelled) return this.finish(run, 'cancelled', '下载被取消');

      const missing = run.round === 1 ? null : this.missingIndexes(run);
      if (missing !== null && missing.length === 0) break;
      if (missing !== null) {
        this.emitProgress(run, 'retrying', missing, `有 ${missing.length} 个参数没收到，按 index 重试`);
      }

      const sent = missing === null ? await this.requestList() : await this.requestMissing(run, missing);
      if (run.cancelled) return this.finish(run, 'cancelled', '下载被取消');
      if (!sent) return this.finish(run, 'failed', '链路未打开或发送失败');

      run.lastProgressAt = this.now();
      const outcome = await this.waitForRound(run);
      if (outcome === 'cancelled') return this.finish(run, 'cancelled', '下载被取消');
      if (this.missingCount(run) === 0 && run.total !== null) {
        return this.finish(run, 'complete', null);
      }
    }

    if (run.cancelled) return this.finish(run, 'cancelled', '下载被取消');
    const missing = this.missingIndexes(run);
    if (run.total === null) {
      return this.finish(
        run,
        run.received.size === 0 ? 'failed' : 'partial',
        run.received.size === 0
          ? '没有收到任何 PARAM_VALUE（链路不通，或对端不响应 PARAM_REQUEST_LIST）'
          : '飞控没有自报参数总数，无法确认是否收全',
      );
    }
    if (missing.length === 0) return this.finish(run, 'complete', null);
    return this.finish(run, 'partial', `还有 ${missing.length} 个参数没有收到，重试 ${this.maxRounds - 1} 次后仍缺失`);
  }

  private async requestList(): Promise<boolean> {
    return this.sendMessage('PARAM_REQUEST_LIST', {
      target_system: this.vehicle.systemId,
      target_component: this.vehicle.componentId,
    });
  }

  private async requestMissing(run: DownloadRun, indexes: readonly number[]): Promise<boolean> {
    for (const index of indexes) {
      // 取消之后不能再往下发：用户切了机型/关了页面，旧机体的链路上不该再出现我们的补缺请求
      if (this.disposed || run.cancelled) return true;
      // 逐个 await：写入本身就是异步的（串口有背压），天然就是节流，
      // 不需要再叠一层定时器；一千条 index 的补缺请求不会一次性灌进链路
      const sent = await this.sendMessage('PARAM_REQUEST_READ', {
        target_system: this.vehicle.systemId,
        target_component: this.vehicle.componentId,
        param_index: index,
        param_id: '',
      });
      if (!sent) return false;
    }
    return true;
  }

  private waitForRound(run: DownloadRun): Promise<'complete' | 'idle' | 'cancelled'> {
    return new Promise((resolve) => {
      run.wake = (outcome) => {
        run.wake = null;
        this.clearIdleTimer(run);
        resolve(outcome);
      };
      if (run.cancelled) {
        run.wake('cancelled');
        return;
      }
      // 极端情况：请求刚发出去时已经收齐了（例如上一轮的补缺帧刚好在这一刻到达）
      if (run.total !== null && this.missingCount(run) === 0) {
        run.wake('complete');
        return;
      }
      this.armIdleCheck(run);
    });
  }

  /**
   * 静默检查。
   *
   * 判据是"**距最后一条新参数**已经过了 `timeoutMs`"，所以到期时间每次都由
   * `lastProgressAt + timeoutMs` 重算，而不是"每次收到参数就 clearTimeout + setTimeout"：
   * 后者在 1600 条参数下要来回 3200 次，而且会把结束判定拖长成"最后一条之后还要再等两轮"。
   * 到期时如果发现期间又收到过参数，就按新的判据重算一次剩余时间。
   */
  private armIdleCheck(run: DownloadRun): void {
    const dueAt = run.lastProgressAt + run.timeoutMs;
    const delay = Math.max(0, dueAt - this.now());
    run.idleTimer = this.scheduler.after(delay, () => {
      run.idleTimer = null;
      if (run.wake === null) return;
      if (run.cancelled) {
        run.wake('cancelled');
        return;
      }
      if (run.lastProgressAt + run.timeoutMs > this.now()) {
        this.armIdleCheck(run);
        return;
      }
      run.wake('idle');
    });
  }

  private clearIdleTimer(run: DownloadRun): void {
    run.idleTimer?.();
    run.idleTimer = null;
  }

  private missingCount(run: DownloadRun): number {
    if (run.total === null) return 0;
    return Math.max(0, run.total - run.received.size);
  }

  private missingIndexes(run: DownloadRun): readonly number[] {
    if (run.total === null) return [];
    const missing: number[] = [];
    for (let index = 0; index < run.total; index += 1) {
      if (!run.received.has(index)) missing.push(index);
    }
    return missing;
  }

  private finish(run: DownloadRun, status: ParamDownloadStatus, reason: string | null): ParamDownloadResult {
    const missing = this.missingIndexes(run);
    const result: ParamDownloadResult = Object.freeze({
      status,
      received: run.received.size,
      total: run.total,
      missing: Object.freeze(missing),
      rounds: this.currentRound(run),
      elapsedMs: Math.max(0, this.now() - run.startedAt),
      reason,
    });
    this.emitProgress(run, status, status === 'partial' ? missing : null, reason);
    return result;
  }

  /**
   * 当前轮次（从 1 开始）。
   *
   * 循环变量在退出时会自增到 `maxRounds + 1`，直接上报它会得到"重试了 4 次"这种
   * 与实际不符的数字（只发了 3 轮请求），所以在这里夹一下。
   */
  private currentRound(run: DownloadRun): number {
    return Math.min(Math.max(run.round, 1), this.maxRounds);
  }

  private emitProgress(
    run: DownloadRun,
    phase: ParamDownloadPhase,
    missingIndexes: readonly number[] | null,
    reason: string | null,
  ): void {
    run.phase = phase;
    this.events.emit('download', {
      phase,
      received: run.received.size,
      total: run.total,
      missing: this.missingCount(run),
      round: this.currentRound(run),
      at: this.now(),
      missingIndexes,
      reason,
    });
  }

  // -------------------------------------------------------------------------
  // 写入
  // -------------------------------------------------------------------------

  /**
   * 写一条参数，并**等飞控回读确认**。
   *
   * 返回 `'applied'` 才代表真的写进去了。其余状态都是"没写成功"，
   * 调用方必须把它们当作失败展示（`'value-mismatch'` 时看 `actual` 是多少）。
   */
  async set(name: string, value: number, options: ParamSetOptions = {}): Promise<ParamSetResult> {
    const startedAt = this.now();
    const trimmed = name.trim();
    const makeResult = (
      status: ParamSetStatus,
      actual: number | null,
      message: string | null,
      warnings: readonly ParamValidationIssueLike[] = [],
    ): ParamSetResult => ({
      status,
      name: trimmed,
      requested: value,
      actual,
      at: this.now(),
      elapsedMs: Math.max(0, this.now() - startedAt),
      message,
      warnings,
    });

    if (this.disposed) return makeResult('disposed', null, '管理器已销毁');
    if (trimmed.length === 0) return makeResult('invalid', null, '参数名不能为空');
    if (!isFiniteParamValue(value)) {
      return makeResult('invalid', null, `参数值必须是有限数，收到 ${String(value)}`);
    }

    const validation = validateParamValue(value, this.metadata?.get(trimmed) ?? null);
    if (validation.blocking !== null) {
      return makeResult(
        validation.blocking === 'read-only' ? 'read-only' : 'invalid',
        this.entries.get(trimmed)?.value ?? null,
        validation.blockingMessage,
        validation.warnings,
      );
    }

    const timeoutMs = options.timeoutMs ?? this.setTimeoutMs;
    const warnings = validation.warnings;

    return new Promise<ParamSetResult>((resolve) => {
      const pending: PendingSet = {
        name: trimmed,
        requested: value,
        at: startedAt,
        warnings,
        sentAt: null,
        timer: null,
        settled: false,
        settle: (status, actual, message) => {
          if (pending.settled) return;
          pending.settled = true;
          pending.timer?.();
          pending.timer = null;
          this.pendingSets = this.pendingSets.filter((item) => item !== pending);
          this.releaseSubscription();
          const result = makeResult(status, actual, message, warnings);
          this.events.emit('set', result);
          resolve(result);
        },
      };

      /*
       * 先订阅、再发送。
       * 反过来的话，回读帧可能在订阅建立之前就被派发掉，
       * 于是这次写入会诡异地"超时"——而且只在链路特别快的时候出现。
       */
      this.pendingSets.push(pending);
      this.retainSubscription();
      void this.writeParamSet(pending, value, timeoutMs);
    });
  }

  private async writeParamSet(pending: PendingSet, value: number, timeoutMs: number): Promise<void> {
    const state = this.vehicle.session.state;
    if (state === 'idle' || state === 'error') {
      pending.settle('send-failed', null, '链路未打开，参数没有发出去');
      return;
    }
    /*
     * 时间戳必须在**发送之前**打。
     *
     * 反直觉之处：回读可能在 `await session.send()` 还没返回时就到达 ——
     * 模拟机、USB 直连、局域网网关都是"写出去的对端立刻回话"，
     * `session.send()` 内部的 `await transport.write()` 一交出控制权，
     * 整条接收链（对端 → 传输 → 解析器 → 事件总线 → 这里）就同步跑完了。
     * 若在发送**之后**才记 `sentAt`，这一帧就会被当成"写入之前就在路上的旧值"而丢弃，
     * 于是每一次成功的写入都会被报成超时失败。
     */
    pending.sentAt = this.now();
    const sent = await this.sendMessage('PARAM_SET', {
      target_system: this.vehicle.systemId,
      target_component: this.vehicle.componentId,
      param_id: pending.name,
      param_value: value,
      param_type: paramTypeReal32(),
    });
    if (!sent) {
      pending.settle('send-failed', null, 'PARAM_SET 发送失败');
      return;
    }
    if (pending.settled) return;
    pending.timer = this.scheduler.after(timeoutMs, () => {
      const known = this.entries.get(pending.name)?.value;
      pending.settle(
        'no-response',
        null,
        `等待 ${timeoutMs} ms 没有收到 ${pending.name} 的回读 PARAM_VALUE，无法确认是否写入成功` +
          (known === undefined ? '' : `（该参数当前已知值是 ${text(known)}，但这次写入没有任何回读）`) +
          '。MAVLink 的 PARAM_SET 没有 COMMAND_ACK，飞控的回应就是再发一条 PARAM_VALUE；' +
          '如果参数名不存在，飞控会直接忽略这条请求',
      );
    });
  }

  /**
   * 把一组参数写进飞控（导入参数文件的落地动作）。
   *
   * 顺序写入、逐条等确认：飞控的参数写队列很深，但**不会**告诉你"这一批里哪几条失败了"，
   * 所以只能一条一条等回读。默认跳过没变化的项 —— 1600 条参数里有 1500 条是默认值，
   * 全部重写要花好几分钟，而用户只是想把备份里改过的那几项恢复回去。
   */
  async apply(
    entries: readonly ParamFileEntry[],
    options: ParamApplyOptions = {},
  ): Promise<ParamApplyReport> {
    const skipUnchanged = options.skipUnchanged ?? true;
    const stopOnFailure = options.stopOnFailure ?? false;
    const intervalMs = options.intervalMs ?? DEFAULT_APPLY_INTERVAL_MS;

    let applied = 0;
    let failed = 0;
    let skipped = 0;
    let stoppedEarly = false;
    const results: ParamSetResult[] = [];

    const emit = (lastResult: ParamSetResult | null): void => {
      this.events.emit('apply', {
        done: applied + failed + skipped,
        total: entries.length,
        applied,
        failed,
        skipped,
        lastResult,
        at: this.now(),
      });
    };

    emit(null);
    for (const entry of entries) {
      if (this.disposed) {
        stoppedEarly = true;
        break;
      }
      const current = this.entries.get(entry.name);
      if (skipUnchanged && current !== undefined && paramValuesEqual(current.value, entry.value)) {
        skipped += 1;
        emit(null);
        continue;
      }
      const result = await this.set(
        entry.name,
        entry.value,
        options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs },
      );
      results.push(result);
      if (result.status === 'applied') applied += 1;
      else failed += 1;
      emit(result);
      if (result.status !== 'applied' && stopOnFailure) {
        stoppedEarly = true;
        break;
      }
      if (intervalMs > 0) await this.sleep(intervalMs);
    }

    return {
      total: entries.length,
      applied,
      failed,
      skipped,
      results: Object.freeze(results),
      stoppedEarly,
    };
  }

  /**
   * 两条参数之间的间隔。让出调用栈而不是空转。
   *
   * 这里**注册了取消**（`applySleep`），而不是把 `scheduler.after()` 的返回值丢掉：
   * 丢掉之后 `dispose()` 没法立刻结束这次等待，于是
   *   - 销毁后 `apply()` 还要多挂最多一个 interval 才返回（界面上的"正在写入"继续转）；
   *   - 假时钟的测试里时间不推进，这个 Promise 就永远不结束，调用方直接卡死。
   */
  private sleep(ms: number): Promise<void> {
    if (this.disposed || ms <= 0) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const cancel = this.scheduler.after(ms, () => {
        this.applySleep = null;
        resolve();
      });
      this.applySleep = { cancel, resolve };
    });
  }

  /** 立刻结束正在等待的写入间隔：先摘掉定时器再放行，避免"已经销毁了还在等时间" */
  private endSleep(): void {
    const pending = this.applySleep;
    if (pending === null) return;
    this.applySleep = null;
    pending.cancel();
    pending.resolve();
  }

  // -------------------------------------------------------------------------
  // 生命周期
  // -------------------------------------------------------------------------

  /**
   * 停掉一切：取消下载、收掉写入间隔的定时器、让等待中的写入立刻失败、摘掉全部订阅。
   *
   * “收掉定时器”这条容易被忽略却同样重要：管理器持有的每个定时器都是资源，
   * 销毁后还留着它就等于"已经销毁的对象仍然会推进"（见 `sleep()` 的说明）。
   *
   * 切机型 / 关页面时必须调用 —— 否则旧车辆的事件总线上还挂着这个管理器的回调，
   * 表现是"切来切去之后界面越来越慢"，且不会报任何错。
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.cancel();
    // 结束 `apply()` 的间隔等待：不结束的话它会带着一个活着的定时器再挂一个 interval，
    // 而且调用方（界面上的"正在写入"）要等到那个定时器到期才知道该收尾
    this.endSleep();
    for (const pending of [...this.pendingSets]) {
      pending.settle('disposed', null, '管理器已销毁，未确认的写入结果未知');
    }
    this.subscriptionCount = 0;
    this.offParamValues?.();
    this.offParamValues = null;
    this.events.clear();
  }

  // -------------------------------------------------------------------------
  // 内部：帧处理
  // -------------------------------------------------------------------------

  private handleParamValue(event: ParamValueEvent): void {
    const existing = this.entries.get(event.name);
    const previous = existing?.value ?? null;
    const changed = existing === undefined || !paramValuesEqual(existing.value, event.value);
    // `Vehicle` 已经把缺失的 param_index 兜底成 0（见 vehicle.ts 的 PARAM_VALUE 分支），
    // 所以这里只能按它给的用；负数是不可能的取值
    const index = event.index >= 0 ? event.index : null;

    const entry: MutableEntry = existing ?? {
      name: event.name,
      value: event.value,
      index,
      type: event.type,
      at: event.at,
    };
    entry.value = event.value;
    entry.index = index;
    entry.type = event.type;
    entry.at = event.at;
    if (existing === undefined) this.entries.set(event.name, entry);
    if (index !== null) this.indexToName.set(index, event.name);
    if (event.count > 0) this.totalParams = event.count;

    const run = this.run;
    const source: ParamChangeSource =
      run !== null && !run.cancelled ? 'download' : this.pendingSets.some((item) => item.name === event.name) ? 'set' : 'value';

    /*
     * 先结算等待中的 PARAM_SET。
     * 这是一次写入的**权威回答**，而 `changed` 只是状态通知；
     * 反过来的话，界面会先看到"值变了"再看到"写入失败"（或反之），
     * 用户看到的是自相矛盾的两条信息。
     */
    this.settlePendingSets(event);

    if (changed) {
      this.events.emit('changed', {
        name: event.name,
        value: event.value,
        previous,
        index,
        source,
        at: event.at,
      });
    }

    if (run !== null && !run.cancelled) this.noteDownloadReceipt(run, index, event.count, event.at);
  }

  /**
   * 下载进度记账。
   *
   * **重传的参数不重复计数、也不重复触发"变化"**（`changed` 已在上面按值判等去重）：
   * 真实链路里同一帧被推两遍（重传、或我们的 `PARAM_REQUEST_READ` 与列表流重叠）很常见，
   * 若按条计进度，进度条会长过 100%、缺口计算也会错。
   */
  private noteDownloadReceipt(
    run: DownloadRun,
    index: number | null,
    count: number,
    at: number,
  ): void {
    // `param_count` 由飞控在每一条 PARAM_VALUE 里自报。取最新一次的非零值：
    // 下载速度慢时飞控可能中途重启（参数表变了），那时应该以它最新报的为准。
    if (count > 0) run.total = count;
    if (index === null) return;
    if (run.total !== null && index >= run.total) return; // 超过自报总数的 index 不参与记账
    if (run.received.has(index)) return;
    run.received.add(index);
    run.lastProgressAt = at > 0 ? at : this.now();
    this.emitProgress(run, 'downloading', null, null);
    if (run.total !== null && this.missingCount(run) === 0) run.wake?.('complete');
  }

  private settlePendingSets(event: ParamValueEvent): void {
    for (const pending of [...this.pendingSets]) {
      if (pending.name !== event.name) continue;
      // 发送之前推过来的值不是这次写入的回读（可能来自并行的下载流），
      // 用它判"成功"会把一次失败的写入误报成成功
      if (pending.sentAt === null || event.at < pending.sentAt) continue;
      if (paramValuesEqual(pending.requested, event.value)) {
        pending.settle('applied', event.value, null);
        continue;
      }
      pending.settle(
        'value-mismatch',
        event.value,
        `飞控回读的值与写入值不一致：写入 ${text(pending.requested)}，回读 ${text(event.value)}` +
          '（说明固件拒绝了这次写入，或按取值范围做了钳制）',
      );
    }
  }

  // -------------------------------------------------------------------------
  // 内部：工具
  // -------------------------------------------------------------------------

  private async sendMessage(name: string, values: MessageValues): Promise<boolean> {
    /*
     * 先让出调用栈再写，理由与 `Vehicle.sendCommandLong()` 完全相同：
     * 参数管理器大量在帧回调里发送（收到 `PARAM_VALUE` 就决定要不要补缺），
     * 如果直接在同一个调用栈里写出去，帧处理与发送会互相嵌套 ——
     * 已经交付的帧会被重复交付，而这种错位只在偶发时序里出现。
     */
    await Promise.resolve();
    if (this.disposed) return false;
    try {
      await this.vehicle.session.send(name, values);
      return true;
    } catch {
      return false;
    }
  }

  private sortedEntries(): readonly MutableEntry[] {
    return [...this.entries.values()].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  }

  private toListItem(entry: MutableEntry): ParamListItem {
    const metadata = this.metadata?.get(entry.name) ?? null;
    return {
      name: entry.name,
      value: entry.value,
      index: entry.index,
      type: entry.type,
      at: entry.at,
      metadata,
      label: metadata?.displayName ?? entry.name,
      units: metadata?.units ?? null,
      descriptionZh: metadata?.descriptionZh ?? null,
      description: metadata === null ? null : metadata.description,
      group: metadata?.group ?? null,
      groupLabel: metadata?.groupLabel ?? null,
      readOnly: metadata?.readOnly ?? false,
      rebootRequired: metadata?.rebootRequired ?? false,
    };
  }
}

function matchesItem(item: ParamListItem, needle: string): boolean {
  if (item.name.toUpperCase().includes(needle)) return true;
  if (item.label.toUpperCase().includes(needle)) return true;
  if (item.groupLabel !== null && item.groupLabel.toUpperCase().includes(needle)) return true;
  if (item.descriptionZh !== null && item.descriptionZh.includes(needle)) return true;
  return false;
}
